import { memo, useState, useCallback } from "react";
import { Modal } from "rsuite";
import { Modal as ConfirmModal } from "../../../components";
import BestStartButton from "../../../components/button";
import { IoSaveOutline } from "react-icons/io5";
import { MdDeleteOutline } from "react-icons/md";
import { formatValue } from "../helpers";

function GoalDialog({
  goalDialog,
  isGoalSaving,
  isGoalDeleting,
  onClose,
  onUpdateField,
  onSave,
  onDelete,
}) {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const openConfirmDelete = useCallback(() => setConfirmDeleteOpen(true), []);
  const closeConfirmDelete = useCallback(() => setConfirmDeleteOpen(false), []);

  const handleConfirmDelete = useCallback(() => {
    setConfirmDeleteOpen(false);
    onDelete?.();
  }, [onDelete]);

  const canDelete = !goalDialog.readOnly && !!goalDialog.form?.id;

  return (
    <>
    <Modal
      backdrop="static"
      keyboard={!isGoalSaving && !isGoalDeleting}
      open={goalDialog.open}
      onClose={onClose}
      size="md"
      className="scorecard-goal-modal"
    >
      <Modal.Header>
        <Modal.Title>{goalDialog.readOnly ? "View KPI focus" : goalDialog.form?.id ? "Edit KPI focus" : "Add KPI focus"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {goalDialog.form && (
          <div className="scorecard-goal-modal-body">
            <section className="scorecard-goal-hero">
              <div className="scorecard-goal-hero-content">
                <div>
                  <span className="home-operations-label">Current focus area</span>
                  <h4>{goalDialog.itemLabel}</h4>
                  <p>{goalDialog.groupLabel}</p>
                </div>
                {goalDialog.itemValue != null && (
                  <span className="scorecard-goal-hero-value">
                    {formatValue(goalDialog.itemValue, { formatting: goalDialog.itemFormatting, type: goalDialog.itemType })}
                  </span>
                )}
              </div>
            </section>

            <section className="scorecard-goal-grid">
              <label className="scorecard-goal-field">
                <span>Impact or risk</span>
                <textarea
                  rows={3}
                  value={goalDialog.form.impactRisk ?? ""}
                  onChange={(event) => onUpdateField("impactRisk", event.target.value)}
                  placeholder="What is the impact or risk if this KPI does not improve?"
                  readOnly={goalDialog.readOnly}
                />
              </label>

              <label className="scorecard-goal-field">
                <span>Target</span>
                <textarea
                  rows={3}
                  value={goalDialog.form.target ?? ""}
                  onChange={(event) => onUpdateField("target", event.target.value)}
                  placeholder="What outcome or target are you aiming for?"
                  readOnly={goalDialog.readOnly}
                />
              </label>

              <label className="scorecard-goal-field scorecard-goal-field-wide">
                <span>Specific action</span>
                <textarea
                  rows={4}
                  value={goalDialog.form.specificAction ?? ""}
                  onChange={(event) => onUpdateField("specificAction", event.target.value)}
                  placeholder="List the next concrete actions for this KPI."
                  readOnly={goalDialog.readOnly}
                />
              </label>
            </section>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="scorecard-goal-modal-footer">
           {!goalDialog.readOnly && (
          <BestStartButton
            aria-label={isGoalSaving ? "Saving KPI focus" : "Save KPI focus"}
            className="scorecard-goal-action"
            color="green"
            content={<><IoSaveOutline aria-hidden="true" size={16} /><span>{isGoalSaving ? "Saving..." : "Save focus"}</span></>}
            height={38}
            onClick={onSave}
            disabled={isGoalSaving || isGoalDeleting || !goalDialog.form}
            width="100%"
          />
        )}
        {canDelete && (
          <BestStartButton
            aria-label={isGoalDeleting ? "Deleting KPI focus" : "Delete KPI focus"}
            className="scorecard-goal-action"
            color="red"
            content={<><MdDeleteOutline aria-hidden="true" size={16} /><span>{isGoalDeleting ? "Deleting..." : "Delete focus"}</span></>}
            height={38}
            onClick={openConfirmDelete}
            disabled={isGoalSaving || isGoalDeleting}
            width="100%"
          />
        )}
        <BestStartButton
          text={goalDialog.readOnly ? "Close" : "Cancel"}
          aria-label={goalDialog.readOnly ? "Close KPI focus" : "Cancel KPI focus"}
          className="scorecard-goal-action"
          color="blue"
          height={38}
          onClick={onClose}
          disabled={isGoalSaving || isGoalDeleting}
          width="100%"
        />
     
      </Modal.Footer>
    </Modal>

    <ConfirmModal
      title="Delete KPI focus?"
      text="This will permanently remove this KPI focus. This action cannot be undone."
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      confirmColor="red"
      cancelColor="blue"
      onConfirm={handleConfirmDelete}
      onClose={closeConfirmDelete}
      open={confirmDeleteOpen}
      loading={isGoalDeleting}
    />
    </>
  );
}

export default memo(GoalDialog);
